'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { useEffect, useState } from 'react';

import { createOrRetrieveInvestmentAndPushToRightInvestSteps } from '@/app/[locale]/(business)/projects/[slug]/_components/_blocks/invest/action';
import { useAuthContext } from '@/app/[locale]/_providers/auth';
import { Button } from '@/app/_components/ui/button';
import { useToast } from '@/app/_components/ui/use-toast';
import { DetailedProject } from '@/domain/project/types';
import { CampaignStatus } from '@/domain/user/services/types';
import analyticsManager from '@/lib/analytics';
import { transformDetailedProjectToECommerceItem } from '@/lib/analytics/utils';
import { getTimeLeft, TimeLeft } from '@/lib/date';
import Routes from '@/lib/router/routes';
import { useRouter } from '@/lib/translation/hooks';

interface InvestCtaProps {
  project: Pick<
    DetailedProject,
    'id' | 'name' | 'ticketMini' | 'slug' | 'status' | 'startDate'
  > & {
    company: Pick<DetailedProject['company'], 'name'>;
  };
  isInvestor: boolean;
  disableInvestCta: boolean;
}

const InvestCta = ({
  project,
  isInvestor,
  disableInvestCta,
}: InvestCtaProps) => {
  const t = useTranslations();
  const { user } = useAuthContext();
  const { toast } = useToast();
  const router = useRouter();
  const [{ days, hours, minutes }, setDisplayValues] = useState<TimeLeft>(
    getTimeLeft(project.startDate),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayValues(getTimeLeft(project.startDate));
    }, 1000);

    return () => clearInterval(interval);
  }, [project.startDate]);

  const isPageLocked = !user;

  const { slug } = project;

  if (project.status === CampaignStatus.Study) {
    return (
      <Button className="relative w-full" type="button" disabled>
        <div className="flex flex-col gap-1">
          <div>{t('pages.project.blocks.invest.cantInvestYetCta')}</div>
          <div>
            {t('pages.project.blocks.invest.cantInvestYetTime', {
              days,
              hours,
              minutes,
            })}
          </div>
        </div>
        <Image
          className="absolute right-2 top-2 size-4 rotate-90"
          width={16}
          height={16}
          src="/white-corner.svg"
          role="presentation"
          alt=""
        />
      </Button>
    );
  }

  if (project.status !== CampaignStatus.Visible) {
    return (
      <Button className="relative w-full" type="button" size="xl" disabled>
        {t('pages.project.blocks.invest.closedCta')}
        <Image
          className="absolute right-2 top-2 size-4 rotate-90"
          width={16}
          height={16}
          src="/white-corner.svg"
          role="presentation"
          alt=""
        />
      </Button>
    );
  }

  if (disableInvestCta) {
    return (
      <Button
        className="relative w-full"
        type="button"
        size="xl"
        onClick={() => router.push(Routes.Profile)}
      >
        {t('pages.project.blocks.invest.verifyFirstCta')}
        <Image
          className="absolute right-2 top-2 size-4 rotate-90"
          width={16}
          height={16}
          src="/white-corner.svg"
          role="presentation"
          alt=""
        />
      </Button>
    );
  }

  const ctaText = isInvestor
    ? t('pages.project.blocks.invest.investorCta')
    : t('pages.project.blocks.invest.cta');

  const handleClick = async () => {
    analyticsManager.client.sendEvent({
      event: 'click_cta',
      link_text: ctaText,
      link_category: 'button',
      link_url: '',
    });
    analyticsManager.client.sendEvent({
      event: 'add_to_cart',
      ecommerce: {
        items: [transformDetailedProjectToECommerceItem(project)],
      },
    });
    try {
      if (project.status !== CampaignStatus.Visible) {
        toast({
          title: t('pages.project.blocks.invest.error.title'),
          description: t(`pages.project.blocks.invest.error.cantInvest`),
          variant: 'destructive',
        });
        return;
      }
      const route = await createOrRetrieveInvestmentAndPushToRightInvestSteps(
        slug,
        user,
      );
      router.push(route);
    } catch (error) {
      const key =
        error instanceof Error ? 'project-not-ready-for-investment' : 'unknown';
      toast({
        title: t('pages.project.blocks.invest.error.title'),
        description: t(`pages.project.blocks.invest.error.${key}`),
        variant: 'destructive',
      });
    }
  };

  return (
    <Button
      className="relative w-full"
      type="button"
      size="xl"
      onClick={handleClick}
      disabled={isPageLocked}
    >
      {ctaText}
      <Image
        className="absolute right-2 top-2 size-4 rotate-90"
        width={16}
        height={16}
        src="/white-corner.svg"
        role="presentation"
        alt=""
      />
    </Button>
  );
};

export default InvestCta;
